<template>
  <!-- Widget -->
  <div ref="el" class="recaptcha"></div>
  <!-- Input field -->
  <HiddenField type="hidden" :name="name" :modelValue="value" />
  <!-- Error alert -->
  <UIAlert
    v-if="context.error.value"
    dismissible
    variant="danger"
    @dismiss="context.error.value = null"
  >
    {{ $t(`form.field.recaptcha.invalid.${context.error.value}`) }}
  </UIAlert>
</template>

<script lang="ts">
import HiddenField from "@/components/fields/HiddenField.vue";
import { RecaptchaSize, RecaptchaTheme } from "./utils";

export type RecaptchaProps = {
  /**
   * Name of the input field. default to `g-recaptcha-response`.
   */
  name?: string;
  /**
   * Sitekey provided by Google.
   * If undefined in component it will use the one defined in the plugin options.
   */
  sitekey?: string;
  /**
   * Optional. The size of the widget. Default to `normal`.
   */
  size?: RecaptchaSize;
  /**
   * Optional. The color theme of the widget. Default to `light`.
   */
  theme?: RecaptchaTheme;
  /**
   * Optional. The tabindex of the widget and challenge. Default to `0`.
   */
  tabindex?: number;
};

export type RecaptchaEvents = {
  /**
   * Executed when the user submits a successful response.
   */
  (event: "success", token: string): void;
  /**
   * Executed when the reCAPTCHA response expires and the user needs to re-verify.
   */
  (event: "expired"): void;
  /**
   * Executed when reCAPTCHA encounters an error.
   */
  (event: "error", reason: RecaptchaErrorType): void;
};
</script>

<script lang="ts" setup>
import UIAlert from "@/components/UI/UIAlert.vue";
import { ref, watch } from "vue";
import { RecaptchaErrorType, provideRecaptchaWidget } from "./useRecaptcha";

const props = withDefaults(defineProps<RecaptchaProps>(), {
  name: "g-recaptcha-response",
});
const emit = defineEmits<RecaptchaEvents>();

const value = ref<string | null | undefined>();

const el = ref<HTMLElement>();

const context = provideRecaptchaWidget(el, {
  sitekey: props.sitekey,
  size: props.size,
  theme: props.theme,
  tabindex: props.tabindex,
});

watch(context.token, (token) => {
  value.value = token;

  if (token) {
    emit("success", token);
  }
});

watch(context.error, (reason) => {
  value.value = null;

  if (reason) {
    emit("error", reason);
  }
});

defineExpose(context);
</script>
